<script setup lang="ts">
import {
  onBeforeUnmount, onMounted, ref, computed, nextTick,
} from 'vue';
import { useRouter } from 'vue-router';
import DataTable, { DataTableRowClickEvent, DataTableFilterEvent, DataTableSortEvent, DataTablePageEvent } from 'primevue/datatable';
import Column from 'primevue/column';
import Modal from '@apparatix/Components/Modal.vue';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import Avatar from 'primevue/avatar';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Menu from 'primevue/menu';
import MultiSelect from '@apparatix/library/MultiSelect.vue';
import Popover from 'primevue/popover';
import Select from 'primevue/select';
import SplitButton from 'primevue/splitbutton';
import Toast from 'primevue/toast';
import ToggleSwitch from 'primevue/toggleswitch';
import BellIcon from '@primeicons/vue/bell';
import BuildingIcon from '@primeicons/vue/building';
import CalendarIcon from '@primeicons/vue/calendar';
import EllipsisHIcon from '@primeicons/vue/ellipsis-h';
import EnvelopeIcon from '@primeicons/vue/envelope';
import ExclamationTriangleIcon from '@primeicons/vue/exclamation-triangle';
import FileExportIcon from '@primeicons/vue/file-export';
import FilterSlashIcon from '@primeicons/vue/filter-slash';
import InfoCircleIcon from '@primeicons/vue/info-circle';
import PencilIcon from '@primeicons/vue/pencil';
import PlusIcon from '@primeicons/vue/plus';
import UnlockIcon from '@primeicons/vue/unlock';
import UsersIcon from '@primeicons/vue/users';
import {
  AccountSearchFields,
  useAccounts,
  useActivities,
  useBuildDefaultColumns,
  useColumnConfig,
  useConfirmUnsavedChanges,
  useEndpoint,
  useIndustryCodes,
  useOpportunities,
  useRoutingHelpers,
  useStringFunctions,
  useToastNotifications,
  useUserPreferences,
  useUtilities,
  type UserPreferences,
} from '@apparatix/composables';
import { useCreateActivity } from '@apparatix/composables/useCreateActivity';
import AELimits from '@apparatix/accounts/AELimits.vue';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import { useAccountStore } from '@apparatix/stores/useAccountStore';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import { useOpportunityCreationStore } from '@apparatix/stores/useOpportunityCreationStore';
import { AppStore } from '@apparatix/stores/AppStore';
import type {
  AccountOverview,
  ApiResponseBase as ApiAccountResponseBase,
  GetAccountAssociationResponse,
  User,
  LazyLoadEvent,
} from '@apparatix/types/Account';
import type { ActivityTypeName } from '@apparatix/schemas/Activities';
import type { ApiResponseBase, GetContactDetailResponse } from '@apparatix/types/Contacts';
import type { AssociationAccount, ConflictingAccount } from '@apparatix/types/Leads';
import { button as greenButton } from '@apparatix/theme/Green';
import AccountConflicts from '@apparatix/leads/AccountConflicts.vue';
import AccountLabelTag from '@apparatix/leads/AccountLabelTag.vue';
import AppContainer from '@apparatix/library/AppContainer.vue';
import CreateAccount from './CreateAccount.vue';
import ColumnDisplaySelector from '@apparatix/library/ColumnDisplaySelector.vue';
import { PendingAccount, newAccountPayloadTransformer } from '@apparatix/schemas/Account';
import HeightOptimizer from './HeightOptimizer.vue';
import CreateCampaignWizard from '@apparatix/leads/CreateCampaignWizard.vue';
import Stack from '@apparatix/library/core/stack/Stack.vue';
import Typography from '@apparatix/library/core/typography/Typography.ts';
import { button as greenButtonTheme } from '@apparatix/theme/Green';

const { formatAsCurrency, isEmptyString } = useStringFunctions();
const { showInfo, showSuccess, showError } = useToastNotifications();
const {
  createAccount,
  loadAccounts,
  loadAccountsHome,
  searchAccounts,
  setConflictLevel,
  updateAccount,
} = useAccounts();
const { openCrmDetailFlyout } = useRoutingHelpers();
const { loadOpportunityOptions } = useOpportunities();
const store = useAccountStore();
const opportunityStore = useOpportunityStore();
const creationStore = useOpportunityCreationStore();
const { addActivityComponent, showModal } = useCreateActivity();
const { postEmailActivityUnlessSynced } = useActivities();
const { isFunction } = useUtilities();

const router = useRouter();

const preferences = ref<UserPreferences>();
const { getPreferences } = useUserPreferences();

const selectedUser = ref<User>();
const createAccountModalVisible = ref(false);
const isCreatingAccount = ref(false);
const potentialAccountConflicts = ref<ConflictingAccount[]>([]);
const skipDuplicateCheckOnCreation = ref(false);
const showCreateLeadWizard = ref(false);
const loadingCampaignId = ref(false);

const { industryCodeRequired } = useIndustryCodes();

const dt = ref();

const defaultColumns = useBuildDefaultColumns([
  ['c2Id', 'Id', false],
  ['c2Name', 'Name'],
  ['c2Type', 'Type'],
  ['accountLabels', 'Label', false],
  ['accountOptions', 'Payment Options', false],
  ['agencyName', 'Agency'],
  ['address', 'Address'],
  ['accountPhone', 'Phone (Account)', false],
  ['ownerName', 'Owner'],
  ['primaryContact', 'Primary Contact'],
  ['primaryContactPhone', 'Phone (Primary Contact)', false],
  ['primaryContactEmail', 'Email (Primary Contact)', false],
  ['lastActivityDate', 'Last Activity'],
  ['daysOwned', 'Days Owned', false],
  ['lifetimeValue', 'Lifetime Value'],
  ['office', 'Office', false],
  ['markets', 'Markets', false],
  ['previousOwnerName', 'Previous Owner', false],
  ['status', 'Status', false],
  ['c2Sic', 'Industry', false],
]);

const {
  handleColumnOrderChange,
  handleVisibleColumnChange,
  visibleColumns,
  columnOrder,
} = useColumnConfig({
  visibleItemsKey: 'crm:accounts:visible-columns',
  columnOrderKey: 'crm:accounts:column-order',
  ref: dt,
  defaultColumns: defaultColumns,
});

const moreMenuRef = ref();
const toast = useToast();

const onRowClick = (e: DataTableRowClickEvent) => {
  if (! e.data.readOnly) {
    store.selectedAccount = e.data;
    openCrmDetailFlyout('accounts', 'account', e.data.c2Id);
  } else {
    toast.add({
      severity: 'info', summary: 'Info', detail: 'You are not authorized to view this account summary', life: 3000,
    });
  }
};

const allowConflictDismiss = computed(() =>
  // TODO?: change depending on current user
  true,
);

const hasPendingConflicts = computed(() => potentialAccountConflicts.value.length > 0);

const displayButton = computed(() => {
  if (!preferences.value) {
    return false;
  }
  // Check if User Preference Enabled
  return preferences.value?.crmCreateOpportunityFromAccount ?? false;
});

const showActivityWithContext = (type: ActivityTypeName) => showModal(type, 'accounts', { id: store.selectedAccount.c2Id, name: store.selectedAccount.c2Name, type: 'Account' });

const accountMenuOptions = ref([]);

const baseMenuItems = () => {
  defaultPrimaryHamburgerMenu.value = 'activity';
  const items = [];

  items.push(
    {
      label: 'Add Activity',
      icon: CalendarIcon,
      id: 'addActivity',
      command: () => showActivityWithContext('Task'),
    },
  );
  if (displayButton.value) {
    defaultPrimaryHamburgerMenu.value = 'createOpportunity';
    items.push(
      {
        label: store.canCreateProposalsFromAccount ? 'Create Proposal' : 'Create Opportunity',
        icon: PencilIcon,
        id: 'createOpportunity',
        command: () => chaosButton(),
      },
    );
  }
  items.push(
    {
      label: 'Send Email',
      icon: EnvelopeIcon,
      command: () => onOpenEmailClick(),
    },
    {
      label: 'Protect/Unprotect',
      icon: UnlockIcon,
      command: () => unprotectAccountClick(),
    },
    {
      label: 'Add Reminder',
      icon: BellIcon,
      command: () => showActivityWithContext('Reminder'),
    },
  );
  return items;
};

const onAccountActionsButtonClick = async (data: any, e: MouseEvent) => {
  const triggerElement = e.currentTarget as HTMLElement;

  if (data.readOnly) {
    toast.add({
      severity: 'info',
      summary: 'Info',
      detail: 'You are not authorized to view this account summary',
      life: 3000,
    });
    return;
  }

  // 1) filter out “Create Opportunity/Proposal” for certain c2Type
  const hideCreate = ['Agency', 'Unqualified Advertiser'].includes(data.c2Type);
  const items = baseMenuItems().filter(item =>
    !(hideCreate && item.id === 'createOpportunity'),
  );

  // 2) conditionally append “Change Owner”
  if (store.canChangeOwner) {
    items.push({
      label: 'Change Owner',
      icon: UsersIcon,
      command: () => changeAccountOwnerClick(),
    });
  }

  // 3) set selected + rebuild popup model + open
  store.selectedAccount = data;
  accountMenuOptions.value = [{ label: 'Actions', items }];
  moreMenuRef.value.toggle({ currentTarget: triggerElement } as unknown as MouseEvent);
};

const chaosButton = async () => {
  loadingCampaignId.value = true;
  await createOpportunity();
  loadingCampaignId.value = false;
  if (store.canCreateProposalsFromAccount) {
    createProposal();
  }
  moreMenuRef.value?.hide();
};

const opportunityItems = computed(() => [
  {
    label: 'Create Opportunity',
    icon: PlusIcon,
    class: 'pv-menu-item-primary-text',
    command: () => createOpportunity(),
  },
]);

const campaignId = ref(0);
const campaignName = ref('');
const campaign = ref();

const { makeRequest: makeCreateAutoOpportunityRequest } = useEndpoint({
  type: 'POST',
  url: '/crm/opportunities',
  successField: 'data',
  onSuccess: response => {
    if (opportunityStore.opportunities.Opportunity?.length ?? 0 > 0) {
      opportunityStore.addOpportunity(response.data.opportunity, true);
    }
    campaign.value = response.data.opportunity;
    campaignId.value = response.data.opportunity.id;
    campaignName.value = response.data.opportunity.name;
  },
  toast: { showOnSuccess: false },
});

const openOpportunityFlyout = async () => {
  await loadOpportunityOptions();
  await router.push({ path: `/crm/accounts/detail/opportunity/${campaignId.value}` });
};

const createProposal = () => {
  window.open(`/apx/proposalTool/index-continue.html?advertiser=${store.selectedAccount.c2Id}&cn=${campaignId.value}`, '_blank');
};

const createOpportunity = async () => {
  makeAssociationsEndpoint(`crm/accounts/${store.selectedAccount.c2Id}/associations`);
  await makeAssociationsRequest();
  const primaryId = store.accountAssociations.contacts.find(contact => contact.contactLabels.some(lbl => lbl.contactLabelId === 1)).id;
  const payload = {
    data: {
      opportunity: {
        type: 'Opportunity',
        accountId: store.selectedAccount.c2Id,
        contacts: [
          { id: primaryId, labelIds: [1]},
        ],
      },
    },
  };
  await makeCreateAutoOpportunityRequest(payload);
  if (store.useCrmCampaigns) {
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Opportunity Created - ${campaignName.value}`,
      group: 'opportunityCreated',
      life: 5000,
    });
  }
};

const contactDetail = ref<GetContactDetailResponse>();

const onOpenEmailClick = async () => {
  if (!store.selectedAccount.primaryId) {
    const accountName = isEmptyString(store.selectedAccount.c2Name) ? 'Account' : store.selectedAccount.c2Name;
    showInfo(`No Primary Contact for ${accountName} - please add/create a Primary Contact.`);
    return;
  }
  detailRequestEndpoint(`/crm/contacts/${store.selectedAccount.primaryId}`);
  await makeDetailRequest();
  const email = contactDetail.value.contact.emails[0]?.email ?? '';
  const fullName = isEmptyString(contactDetail.value.contact.fullName) ? 'Primary Contact' : contactDetail.value.contact.fullName;
  if (isEmptyString(email)) {
    showInfo(`No email found for ${fullName} - please add an email.`);
    await new Promise(resolve => setTimeout(resolve, 1000));
  }
  postEmailActivityUnlessSynced(1, store.selectedAccount.c2Id, store.selectedAccount.c2Name, 'accounts');
  window.open(`mailto:${email}`);
};

const { makeRequest: makeDetailRequest, setEndpointUrl: detailRequestEndpoint } = useEndpoint<ApiResponseBase<GetContactDetailResponse>>({
  url: '',
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    contactDetail.value = response.data;
  },
  toast: { showOnSuccess: false },
});

const exportCSV = () => {
  dt.value.exportCSV();
};

const setup = async () => {
  await loadAccountsHome();
  await managePreferences();

  store.isLoading = false;
};


const closeCreateAccountModal = () => {
  createAccountModalVisible.value = false;
  potentialAccountConflicts.value = [];
  skipDuplicateCheckOnCreation.value = false;
};

const handleAccountCreation = async (account: PendingAccount) => {
  isCreatingAccount.value = true;
  const searchComplete = Promise.withResolvers<void>();
  const searchParams: AccountSearchFields = {
    name: account.name,
    mode: 'startswith',
  };

  let hadConflicts = false;

  if (!skipDuplicateCheckOnCreation.value) {
    searchAccounts(searchParams, accounts => {
      if (!accounts?.length) {
        potentialAccountConflicts.value = [];
      } else {
        hadConflicts = true;

        accounts.forEach(acct => {
          setConflictLevel(acct, account);
          potentialAccountConflicts.value.push(acct as ConflictingAccount);
        });
      }
      searchComplete.resolve();
    });
  } else {
    searchComplete.resolve();
  }

  await searchComplete.promise;

  if (!hadConflicts) {
    try {
      const accountPayload = newAccountPayloadTransformer.parse(account);
      const result = await createAccount(accountPayload);
      if (result?.id) {
        store.accounts.unshift({ c2Id: result.id, c2Name: result.name, c2Type: result.displayType } as AccountOverview);
        closeCreateAccountModal();
        showSuccess(`${result.name} was successfully created`, 'Account Created');
      }
    } catch {
      showError('Unable to save account');
    }
  }
  isCreatingAccount.value = false;
};

const onAcceptConflictSuggestion = (suggestion: AssociationAccount) => {
  potentialAccountConflicts.value = [];
  store.filterByName(suggestion.c2Name);
  closeCreateAccountModal();
};

const onCancelConflictSuggestion = () => {
  potentialAccountConflicts.value = [];
  skipDuplicateCheckOnCreation.value = true;
};

const aeLimitsVisible = ref(false);

const isPopoverVisible = ref(false);

const infoButton = ref();

const handlePopover = (event) => {
  infoButton.value.toggle(event);
};

const handleClickOutside = (event) => {
  if (infoButton.value && !infoButton.value.$el.contains(event.target) && !event.target.closest('.custom-popover')) {
    isPopoverVisible.value = false;
  }
};

const confirm = useConfirm();
const unprotectAccountClick = async () => {
  if (store.selectedAccount.c2Type === 'Agency') {
    toast.add({
      severity: 'info', summary: 'Info', detail: 'You can\'t protect or unprotect an agency', life: 3000,
    });
    return;
  }

  if (store.selectedAccount.c2Type === 'Advertiser') {
    toast.add({
      severity: 'info', summary: 'Info', detail: 'You can\'t protect or unprotect an active advertiser', life: 3000,
    });
    return;
  }
  confirm.require({
    group: 'accountUnprotect',
    accept: () => onUnprotectConfirm(),
    position: 'center',
    icon: ExclamationTriangleIcon,
    acceptLabel: 'Save',
    rejectLabel: 'Cancel',
    rejectClass: 'p-button-secondary',
    acceptProps: {
      dt: greenButton,
    },
  });
};

const changeAccountOwnerClick = async () => {
  if (store.selectedAccount.c2Type === 'Agency') {
    toast.add({
      severity: 'info', summary: 'Info', detail: 'You can\'t change owner of an agency', life: 3000,
    });
    return;
  }

  if (store.selectedAccount.c2Type === 'Advertiser') {
    toast.add({
      severity: 'info', summary: 'Info', detail: 'You can\'t change owner on an active advertiser', life: 3000,
    });
    return;
  }
  confirm.require({
    group: 'accountChangeOwner',
    header: 'Select user to change account owner to:',
    accept: () => onChangeOwnerConfirm(),
    position: 'center',
    icon: ExclamationTriangleIcon,
    acceptLabel: 'Save',
    rejectLabel: 'Cancel',
    rejectClass: 'p-button-secondary',
    acceptProps: {
      dt: greenButton,
    },
  });
};

const onUnprotectConfirm = async () => {
  if (store.selectedAccount.ownerName === 'Unprotected') {
    updateAccount({ data: { accounts: [{ id: store.selectedAccount.c2Id , owner: { id: AppStore.currentUser.id }}]}});
    store.selectedAccount.ownerName = AppStore.currentUser.displayName;
  } else {
    updateAccount({ data: { accounts: [{ id: store.selectedAccount.c2Id, owner: store.users.find(user => user.fullName === 'Unprotected') }]}});
    store.selectedAccount.ownerName = 'Unprotected';
  }
};

const onChangeOwnerConfirm = async () => {
  updateAccount({ data: { accounts: [{ id: store.selectedAccount.c2Id , owner: { id: selectedUser.value.id }}]}});
  store.selectedAccount.ownerName = selectedUser.value.fullName;
};

const onShowFiltersChange = (value: boolean) => {
  if (!value) {
    store.filteredAccountLength = store.filteredAccounts.length;
    store.hideTableFilters();
  } else {
    store.showTableFilters();
  }
};

const leadWizardHasUnsavedChanges = ref(false);

const wizardsHaveUnsavedChanges = () => opportunityStore.showOpportunityCreationModal
  ? creationStore.hasUnsavedChanges()
  : leadWizardHasUnsavedChanges.value;

const { component: UnsavedChanges, onClick: handleCloseWizard } = useConfirmUnsavedChanges(wizardsHaveUnsavedChanges, () => {
  showCreateLeadWizard.value = false;
  if (opportunityStore.showOpportunityCreationModal) {
    closeOpportunityCreationModal();
  }
}, 'dialog');

const suppressWizardWarning = ref(false);

const onModalClose = () => {
  if (suppressWizardWarning.value) {
    closeOpportunityCreationModal();
  } else {
    handleCloseWizard();
  }
};

const onCampaignWizardClose = async () => {
  suppressWizardWarning.value = true;
  await nextTick();

  onModalClose();
};

const closeOpportunityCreationModal = () => {
  opportunityStore.showOpportunityCreationModal = false;
  suppressWizardWarning.value = false;
};

const loadingErrors = ref({
  activities: false,
  associations: false,
  details: false,
  locations: false,
  overview: false,
  revenue: false,
});

const {
  makeRequest: makeAssociationsRequest, setEndpointUrl: makeAssociationsEndpoint,
} = useEndpoint<ApiAccountResponseBase<GetAccountAssociationResponse>>({
  url: '',
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    store.accountAssociations = response.data;
    store.hasLinkAccountButton = response.data.permissions.some((permission) => permission.tag === 'hasLinkAccountButton');
    loadingErrors.value.associations = false;
  },
  onError: () => {
    loadingErrors.value.associations = true;
  },
  toast: { showOnSuccess: false },
});

const defaultPrimaryHamburgerMenu = ref<'activity' | 'createOpportunity'>('activity');
const isSetAsPrimary = (id: string) => id === defaultPrimaryHamburgerMenu.value;

const resolveLabel = (label: ((obj: object) => string) | string): string => isFunction(label) ? label({}) : label ?? '';

const managePreferences = async () => {
  const responsePreferences = await getPreferences();
  if (responsePreferences) {
    preferences.value = responsePreferences;
  }
};

onMounted(() => {
  setup();
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});

const rowsPerPageOptions = computed(() => [100, 250, 500, 1000, store.totalRecords]);

const tableFiltersSortField = ref<string | ((item: any) => string) | null>(null);
const tableFiltersSortOrder = ref<number | null>(null);

const onPage = (event: DataTablePageEvent) => {
  const lazyEvt: LazyLoadEvent = {
    first: event.first,
    pageSize: event.rows,
    sortField: tableFiltersSortField.value,
    sortOrder: tableFiltersSortOrder.value,
    filters: store.tableFilters,
  };
  loadAccounts(lazyEvt);
};

const onSort = (event: DataTableSortEvent) => {
  let sortOrder = event.sortOrder;
  if (event.sortField === 'lifetimeValue') {
    sortOrder = sortOrder * -1;
  }

  tableFiltersSortField.value = event.sortField;
  tableFiltersSortOrder.value = sortOrder;

  const lazyEvt: LazyLoadEvent = {
    first: 0,
    pageSize: event.rows,
    sortField: event.sortField,
    sortOrder: sortOrder,
    filters: store.tableFilters,
  };
  loadAccounts(lazyEvt);
};

const DEBOUNCE_MS = 400;
let filterDebounceTimer: number | undefined;

const onFilter = (event: DataTableFilterEvent) => {
  // Take the latest filters immediately (so UI reflects chips, etc.)
  store.tableFilters = { ...(event.filters as any) };

  const lazyEvt: LazyLoadEvent = {
    first: 0,
    pageSize: event.rows,
    sortField: tableFiltersSortField.value,
    sortOrder: tableFiltersSortOrder.value,
    filters: store.tableFilters,
  };

  // debounce network call
  if (filterDebounceTimer) window.clearTimeout(filterDebounceTimer);
  filterDebounceTimer = window.setTimeout(() => {
    loadAccounts(lazyEvt);
  }, DEBOUNCE_MS);
};

const clearFilters = () => {
  store.resetTableFilters();
  const initialEvent: LazyLoadEvent = {
    first: 0,
    pageSize: store.rowsPerPage,
    sortField: null,
    sortOrder: null,
    filters: store.tableFilters,
  };
  loadAccounts(initialEvent);
};

</script>
<template>
  <AppContainer>
    <FormSkeleton v-if="store.isLoading" />
    <template v-else>
      <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-2">
          <Avatar
            size="normal"
            shape="circle"
            style="background-color: var(--p-teal-100);"
          >
            <template #icon>
              <BuildingIcon color="var(--p-teal-500" />
            </template>
          </Avatar>
          <h1 class="text-lg">
            Accounts
          </h1>
          <Badge :value="store.totalRecords" size="large" severity="secondary" />
        </div>
        <div class="flex-1 text-right text-med text-[var(--swimlane-secondary-text)]">
          {{ store.aeLimit?.prospectCount ?? 0 }} Prospects,
          {{ store.aeLimit?.advertiserCount ?? 0 }} Advertisers
          <Button
            id="infoButton"
            icon-only
            size="small"
            class="p-button-text"
            :aria-haspopup="true"
            aria-label="More Information"
            @mouseenter="handlePopover"
            @mouseleave="handlePopover"
            @focus="handlePopover"
            @blur="handlePopover"
          >
            <InfoCircleIcon color="var(--swimlane-secondary-text)" />
          </Button>
          <Popover
            ref="infoButton"
            v-model:visible="isPopoverVisible"
            placement="right"
            class="custom-popover"
          >
            <div class="popover-content w-fit p-2">
              <h3 class="text-base font-medium text-[var(--swimlane-primary-text)]">
                Account Limits
              </h3>
              <div class="text-sm">
                <div class="flex justify-between items-center">
                  <div class="text-left pr-8">
                    {{ store.aeLimit?.prospectCount ?? 0 }} Prospects
                  </div>
                  <div class="text-right text-[var(--swimlane-secondary-text)]">
                    Max {{ store.aeLimit?.prospectLimit ?? 0 }}
                  </div>
                </div>

                <div class="flex justify-between items-center">
                  <div class="text-left pr-8">
                    {{ store.aeLimit?.advertiserCount ?? 0 }} Advertisers
                  </div>
                  <div class="text-right text-[var(--swimlane-secondary-text)]">
                    Max {{ store.aeLimit?.advertiserLimit ?? 0 }}
                  </div>
                </div>

                <div class="flex justify-between items-center">
                  <div class="text-left pr-8">
                    {{ store.aeLimit?.totalCount ?? 0 }} Total
                  </div>
                  <div class="text-right text-[var(--swimlane-secondary-text)]">
                    Max {{ store.aeLimit?.totalLimit ?? 0 }}
                  </div>
                </div>
              </div>
            </div>
          </Popover>
        </div>

        <div class="flex flex-col gap-4 sm:flex-row sm:gap-4">
          <Button
            v-if="store.canViewAELimits"
            v-tooltip.left="'AE Limits'"
            outlined
            severity="secondary"
            class="flex items-center"
            @click="aeLimitsVisible = true"
          >
            <BuildingIcon />
            <span class="font-bold">AE Limits</span>
          </Button>


          <Modal
            title="AE Limits"
            :visible="aeLimitsVisible"
            :disable-accept="true"
            :disable-cancel="true"
            :disable-footer="true"
            @close="aeLimitsVisible = false"
          >
            <template #body>
              <AELimits />
            </template>
          </Modal>


          <Button
            v-tooltip.left="'Export'"
            outlined
            severity="secondary"
            class="flex items-center"
            @click="exportCSV()"
          >
            <FileExportIcon />
            <span class="font-bold">Export</span>
          </Button>
          <Button
            v-if="store.canAddAccounts"
            v-tooltip.left="'Create Account'"
            class="flex items-center"
            @click="createAccountModalVisible = true"
          >
            <PencilIcon />
            <span class="font-bold">Create Account</span>
          </Button>
        </div>
      </div>
      <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center justify-center gap-2">
          <span class="text-center">Filters</span>
          <ToggleSwitch v-model="store.showFilters" @update:model-value="val => onShowFiltersChange(val)" />
          <Button
            v-tooltip.left="'Clear Filters'"
            size="small"
            outlined
            severity="secondary"
            class="flex items-center"
            :disabled="!(store.showFilters && store.isFilterChanged)"
            @click="clearFilters"
          >
            <FilterSlashIcon />
            <span class="font-bold">Clear Filters</span>
          </Button>
        </div>
        <div class="flex items-center justify-center gap-2">
          <ColumnDisplaySelector
            :default-table-columns="defaultColumns"
            :default-visible-columns="defaultColumns.filter(col => col.visible).map(col => col.field)"
            :table-columns="columnOrder"
            :visible-columns="visibleColumns"
            storage-prefix="crm:accounts"
            :data-table="dt"
            @update:visible-columns="handleVisibleColumnChange"
            @update:table-columns="handleColumnOrderChange"
          />
        </div>
      </div>
      <HeightOptimizer>
        <template #default="{availableHeight}">
          <DataTable
            ref="dt"
            v-model:filters="store.tableFilters"
            lazy
            :value="store.accounts"
            :loading="store.isTableLoading"
            :total-records="store.totalRecords"
            :rows="store.rowsPerPage"
            paginator
            :rows-per-page-options="rowsPerPageOptions"
            :data-key="'c2Id'"
            table-style="min-width: 50rem;"
            :virtual-scroller-options="{ itemSize: 50 }"
            :scroll-height="`calc(${availableHeight} - 60px)`"
            table-layout="fixed"
            scrollable
            reorderable-columns
            resizable-columns
            column-resize-mode="expand"
            :filter-display="store.showFilters ? 'row' : null"
            @row-click="onRowClick"
            @filter="onFilter"
            @sort="onSort"
            @page="onPage"
          >
            <Column
              field="c2Id"
              :header="defaultColumns.find(col => col.field === 'c2Id').header"
              sortable
              :hidden="!visibleColumns.includes('c2Id')"
              :reorderable-column="false"
              :filter-match-mode-options="[
                { label: 'Contains', value: 'contains' },
                { label: 'Equals', value: 'equals' },
                { label: 'Less Than', value: 'lt' },
                { label: 'Greater Than', value: 'gt' },
              ]"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Ids"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="c2Name"
              :header="defaultColumns.find(col => col.field === 'c2Name').header"
              sortable
              :hidden="!visibleColumns.includes('c2Name')"
              :reorderable-column="false"
              :filter-match-mode-options="[
                { label: 'Contains', value: 'contains' },
                { label: 'Starts With', value: 'startsWith' },
              ]"
              class="w-3/12"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Accounts"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
              <template #body="slotProps">
                <span class="font-semibold">{{ slotProps.data.c2Name }}</span>
              </template>
            </Column>
            <Column
              field="c2Type"
              :header="defaultColumns.find(col => col.field === 'c2Type').header"
              sortable
              :hidden="!visibleColumns.includes('c2Type')"
              :reorderable-column="false"
              :show-filter-menu="false"
              class="w-1/12"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  :options="store.accountTypes"
                  filter
                  placeholder="Types"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 8rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="accountLabels"
              :header="defaultColumns.find(col => col.field === 'accountLabels').header"
              sortable
              :hidden="!visibleColumns.includes('accountLabels')"
              :reorderable-column="false"
              :show-filter-menu="false"
              :exportable="false"
            >
              <template #body="slotProps">
                <div class="flex flex-wrap gap-1">
                  <AccountLabelTag
                    v-for="(item, index) in slotProps.data.accountLabels"
                    :key="index"
                    :label="item"
                  />
                </div>
              </template>
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  :options="store.labelOptions"
                  filter
                  class="p-column-filter"
                  option-label="value"
                  placeholder="Labels"
                  @change="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="accountOptions"
              sortable
              :header="defaultColumns.find(col => col.field === 'accountOptions').header"
              :hidden="!visibleColumns.includes('accountOptions')"
              :reorderable-column="false"
              :show-filter-menu="false"
              :exportable="false"
            >
              <template #body="slotProps">
                <div
                  v-for="(item, index) in slotProps.data.accountOptions"
                  :key="index"
                  class="inline-flex items-center justify-center py-1 text-white uppercase text-xs rounded-full leading-none px-2 font-bold"
                  :style="{ background: item.color }"
                >
                  {{ item.value }}
                </div>
              </template>
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  :options="store.paymentOptions"
                  filter
                  class="p-column-filter"
                  option-label="value"
                  placeholder="Payments"
                  @change="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="accountOptionsExport"
              sortable
              header="Payment Options"
              :hidden="true"
            />
            <Column
              field="agencyName"
              :header="defaultColumns.find(col => col.field === 'agencyName').header"
              sortable
              :hidden="!visibleColumns.includes('agencyName')"
              :reorderable-column="false"
              :filter-match-mode-options="[
                { label: 'Contains', value: 'contains' },
                { label: 'Starts With', value: 'startsWith' },
              ]"
              class="w-1/12"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Agencies"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="address"
              :header="defaultColumns.find(col => col.field === 'address').header"
              sortable
              :hidden="!visibleColumns.includes('address')"
              :reorderable-column="false"
              :filter-match-mode-options="[
                { label: 'Contains', value: 'contains' },
              ]"
              class="w-1/12"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Address"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="accountPhone"
              :header="defaultColumns.find(col => col.field === 'accountPhone').header"
              sortable
              :hidden="!visibleColumns.includes('accountPhone')"
              :reorderable-column="false"
              :filter-match-mode-options="[
                { label: 'Contains', value: 'contains' },
                { label: 'Starts With', value: 'startsWith' },
              ]"
              class="w-1/12"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Phone"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="ownerName"
              :header="defaultColumns.find(col => col.field === 'ownerName').header"
              sortable
              :hidden="!visibleColumns.includes('ownerName')"
              :reorderable-column="false"
              :show-filter-menu="false"
              class="w-1/12"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  :options="store.accountOwners"
                  filter
                  placeholder="Owners"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 8rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="primaryContact"
              :header="defaultColumns.find(col => col.field === 'primaryContact').header"
              sortable
              :hidden="!visibleColumns.includes('primaryContact')"
              :reorderable-column="false"
              class="w-1/12"
              :filter-match-mode-options="[
                { label: 'Contains', value: 'contains' },
                { label: 'Starts With', value: 'startsWith' },
              ]"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Contacts"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="primaryContactPhone"
              :header="defaultColumns.find(col => col.field === 'primaryContactPhone').header"
              sortable
              :hidden="!visibleColumns.includes('primaryContactPhone')"
              :reorderable-column="false"
              :filter-match-mode-options="[
                { label: 'Contains', value: 'contains' },
                { label: 'Starts With', value: 'startsWith' },
              ]"
              class="w-1/12"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Phone"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="primaryContactEmail"
              :header="defaultColumns.find(col => col.field === 'primaryContactEmail').header"
              sortable
              :hidden="!visibleColumns.includes('primaryContactEmail')"
              :reorderable-column="false"
              :filter-match-mode-options="[
                { label: 'Contains', value: 'contains' },
                { label: 'Starts With', value: 'startsWith' },
              ]"
              class="w-1/12"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Email"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="lastActivityDate"
              :header="defaultColumns.find(col => col.field === 'lastActivityDate').header"
              sortable
              :hidden="!visibleColumns.includes('lastActivityDate')"
              :reorderable-column="false"
              :sort-field="'lastActivityTimeStamp'"
              class="w-2/12"
            />
            <Column
              field="daysOwned"
              :header="defaultColumns.find(col => col.field === 'daysOwned').header"
              sortable
              :hidden="!visibleColumns.includes('daysOwned')"
              :reorderable-column="false"
              :sort-field="'daysOwned'"
              :filter-match-mode-options="[
                { label: 'Equals', value: 'equals' },
                { label: 'Less Than', value: 'lt' },
                { label: 'Greater Than', value: 'gt' },
              ]"
              class="w-2/12"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Days Owned"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="lifetimeValue"
              :header="defaultColumns.find(col => col.field === 'lifetimeValue').header"
              sortable
              :hidden="!visibleColumns.includes('lifetimeValue')"
              :reorderable-column="false"
              class="w-1/12"
              :filter-match-mode-options="[
                { label: 'Less Than', value: 'lt' },
                { label: 'Greater Than', value: 'gt' },
              ]"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Values"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
              <template #body="{ data }">
                {{ formatAsCurrency(data.lifetimeValue ?? 0) }}
              </template>
            </Column>
            <Column
              field="office"
              :header="defaultColumns.find(col => col.field === 'office').header"
              sortable
              :hidden="!visibleColumns.includes('office')"
              :reorderable-column="false"
              :show-filter-menu="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  :options="store.accountOffices"
                  filter
                  placeholder="Offices"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 8rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="markets"
              :header="defaultColumns.find(col => col.field === 'markets').header"
              sortable
              :hidden="!visibleColumns.includes('markets')"
              :reorderable-column="false"
              :show-filter-menu="false"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Markets"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
              <template #body="slotProps">
                {{ slotProps.data.markets?.join(', ') }}
              </template>
            </Column>
            <Column
              field="previousOwnerName"
              :header="defaultColumns.find(col => col.field === 'previousOwnerName').header"
              sortable
              :hidden="!visibleColumns.includes('previousOwnerName')"
              :reorderable-column="false"
              style="width: 0px"
              :show-filter-menu="false"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  :options="store.prevAccountOwners"
                  filter
                  placeholder="Owners"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 8rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="status"
              :header="defaultColumns.find(col => col.field === 'status').header"
              sortable
              :hidden="!visibleColumns.includes('status')"
              :reorderable-column="false"
              style="width: 0px"
            />
            <Column
              field="c2Sic"
              :header="defaultColumns.find(col => col.field === 'c2Sic').header"
              sortable
              :hidden="!visibleColumns.includes('c2Sic')"
              :show-filter-menu="false"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  :options="store.industryCodes"
                  filter
                  placeholder="Industry Codes"
                  class="p-column-filter"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
            </Column>
            <Column
              class="w-1/12"
              :reorderable-column="false"
              frozen
              align-frozen="right"
            >
              <template #body="slotProps">
                <Button
                  v-tooltip.left="'Maintenance'"
                  size="small"
                  rounded
                  class="icon-button"
                  style="background:none; border:none; color:var(--primary-blue)"
                  @click="(event) => onAccountActionsButtonClick(slotProps.data, event)"
                >
                  <EllipsisHIcon size="1.5rem" color="var(--apx-dark-gray-400)" />
                </Button>
              </template>
            </Column>
          </DataTable>
        </template>
      </HeightOptimizer>
      <Menu ref="moreMenuRef" :model="accountMenuOptions" :popup="true">
        <template #item="{ item, props }">
          <div v-if="item.id === 'createOpportunity'" @click.stop>
            <SplitButton
              v-if="store.canCreateProposalsFromAccount && store.useCrmCampaigns"
              :disabled="loadingCampaignId"
              :model="opportunityItems"
              :label="resolveLabel(item.label)"
              button-class="text-primary bg-primary/10 hover:bg-primary/20"
              menu-button-class="text-primary bg-primary/10 hover:bg-primary/20"
              append-to="body"
              @click="chaosButton"
              @dropdown-click.stop
            >
              <template #icon>
                <component :is="item.icon" />
              </template>
            </SplitButton>
            <Button
              v-else-if="!store.canCreateProposalsFromAccount && store.useCrmCampaigns"
              button-class="text-primary bg-primary/10 hover:bg-primary/20"
              menu-button-class="text-primary bg-primary/10 hover:bg-primary/20"
              @click="chaosButton"
              v-on="props.action"
            >
              <component :is="item.icon" />
              {{ resolveLabel(item.label) }}
            </Button>
            <Button
              v-else-if="!store.useCrmCampaigns"
              button-class="text-primary bg-primary/10 hover:bg-primary/20"
              menu-button-class="text-primary bg-primary/10 hover:bg-primary/20"
              @click="chaosButton"
              v-on="props.action"
            >
              <component :is="item.icon" />
              Create Proposal
            </Button>
          </div>
          <div v-else>
            <a
              v-if="isSetAsPrimary(item.id)"
              severity="primary"
              v-bind="props.action"
            >
              <component :is="item.icon" />
              <span> {{ resolveLabel(item.label) }}</span>
            </a>
            <a
              v-else
              v-bind="props.action"
            >
              <component :is="item.icon" />
              <span> {{ resolveLabel(item.label) }}</span>
            </a>
          </div>
        </template>
      </Menu>
    </template>
  </AppContainer>
  <RouterView />

  <ConfirmDialog group="accountUnprotect">
    <template #message="">
      <template v-if="store.selectedAccount.ownerName === 'Unprotected'">
        Add {{ store.selectedAccount.c2Name }} to your protected list?
      </template>
      <template v-else>
        Remove {{ store.selectedAccount.c2Name }} from {{ store.selectedAccount.ownerName }}'s protected list?
      </template>
    </template>
  </ConfirmDialog>
  <ConfirmDialog group="accountChangeOwner">
    <template #message="">
      <Select
        v-model="selectedUser"
        :options="store.users"
        option-label="fullName"
        class="w-full"
      />
    </template>
  </ConfirmDialog>
  <Modal
    title="Create Account"
    :visible="createAccountModalVisible"
    disable-footer
    @close="closeCreateAccountModal"
  >
    <template #body>
      <div class="flex flex-col gap-6 p-4 w-full lg:w-2/3 lg:mr-auto lg:ml-auto">
        <CreateAccount
          :allowed-selection-types="[]"
          :default-type="'Advertiser'"
          :allowed-creation-types="['Advertiser', 'Agency']"
          :is-loading="isCreatingAccount || hasPendingConflicts"
          hide-title
          :industry-code-required="industryCodeRequired"
          :allow-agency-in-wizard="true"
          @on:submit="handleAccountCreation"
        />
        <AccountConflicts
          select-button-label="View"
          :conflicts="potentialAccountConflicts"
          :allow-dismiss="allowConflictDismiss"
          @account-selected="onAcceptConflictSuggestion"
          @cancel="onCancelConflictSuggestion"
        />
      </div>
    </template>
  </Modal>
  <Modal
    title="Convert Lead to Opportunity"
    :visible="opportunityStore.showOpportunityCreationModal"
    disable-footer
    @close="onModalClose"
  >
    <template #body>
      <CreateCampaignWizard
        :industry-code-required="industryCodeRequired"
        type="Opportunity"
        :edit-lead="opportunityStore.selectedOpportunity['Lead']"
        @close="onCampaignWizardClose"
        @update:has-unsaved-changes="hasChanges => leadWizardHasUnsavedChanges = hasChanges"
      />
    </template>
  </Modal>
  <Toast group="opportunityCreated" :escape="false">
    <template #message="slotProps">
      <Stack :gap="2">
        <Typography variant="body">
          {{ slotProps.message.detail }}
        </Typography>
        <div>
          <Button
            :dt="greenButtonTheme"
            size="small"
            group="opportunityCreated"
            @click="openOpportunityFlyout"
          >
            Open
          </Button>
        </div>
      </Stack>
    </template>
  </Toast>
  <component :is="addActivityComponent" />
  <component :is="UnsavedChanges" />
</template>

    <style scoped>
      .datatable-container {
      /* Ensure the container occupies full height */
      height: 100vh;
      display: flex;
      flex-direction: column;
      }

      :deep(.p-datatable-header) {
      background: none;
      }

      .p-button-secondary {
      background-color: lightgray;
      background: var(--white);
      border: 1px solid var(--black-16);
      color: var(--black-80);
      }

      .p-badge-secondary {
      background-color: lightgray;
      background: var(--white);
      border: 1px solid var(--black-16);
      color: var(--black-80);
      }
    </style>
